import { EventCallback, EventRegistryObject, EventDisposer } from './types';

/**
 * Generic event bus with type-safe event handling
 *
 * This class provides a pub/sub pattern for event handling with full TypeScript support.
 * It allows subscribing to events, firing events, and managing event listeners.
 *
 * Named `EventBus` to avoid conflicts with RxJS Observable.
 *
 * @template EventSpec - Object type mapping event names to their payload types
 *
 * @example
 * ```typescript
 * interface MyEvents {
 *   'user:login': { userId: string; timestamp: number };
 *   'user:logout': { userId: string };
 * }
 *
 * const emitter = new EventBus<MyEvents>();
 *
 * // Subscribe to event
 * const disposer = emitter.on('user:login', (data) => {
 *   console.log(`User ${data.userId} logged in at ${data.timestamp}`);
 * });
 *
 * // Fire event
 * emitter.fire('user:login', { userId: '123', timestamp: Date.now() });
 *
 * // Unsubscribe
 * disposer();
 * ```
 */
export class EventBus<EventSpec> {
  // Handlers are stored as EventCallback<never>: any EventCallback<E> is
  // assignable to it (parameter contravariance), so no casts on write.
  private eventListeners: Record<keyof EventSpec, EventCallback<never>[]> =
    {} as Record<keyof EventSpec, EventCallback<never>[]>;

  /**
   * Subscribes to a specified event
   *
   * @param eventName - Name of the event to subscribe to
   * @param handler - Function to call when the event is fired
   * @returns Disposer function to unsubscribe from the event
   */
  on<K extends keyof EventSpec>(
    eventName: K,
    handler: EventCallback<EventSpec[K]>,
  ): EventDisposer;

  /**
   * Subscribes to multiple events at once
   *
   * @param handlers - Object mapping event names to their handlers
   * @returns Disposer function to unsubscribe from all events
   */
  on(handlers: EventRegistryObject<EventSpec>): EventDisposer;

  on<K extends keyof EventSpec>(
    arg0: K | EventRegistryObject<EventSpec>,
    handler?: EventCallback<EventSpec[K]>,
  ): EventDisposer {
    this.initializeEventListeners();

    // Handle object syntax: { 'event:name': handler, ... }
    if (this.isEventRegistryObject(arg0)) {
      Object.entries(arg0).forEach(([eventName, eventHandler]) => {
        this.on(eventName as K, eventHandler as EventCallback<EventSpec[K]>);
      });
      return () => this.off(arg0);
    }

    // Handle single event subscription
    else if (handler) {
      const eventName = arg0;
      this.ensureEventListenerArray(eventName);
      this.eventListeners[eventName].push(handler);
      return () => this.off(eventName, handler);
    }

    // No-op case
    else return () => false;
  }

  /**
   * Subscribes to an event that will only fire once
   * After the first invocation, the handler is automatically unsubscribed
   *
   * @param eventName - Name of the event to subscribe to
   * @param handler - Function to call when the event is fired (only once)
   * @returns Disposer function to unsubscribe from the event
   */
  once<K extends keyof EventSpec>(
    eventName: K,
    handler: EventCallback<EventSpec[K]>,
  ): EventDisposer;

  /**
   * Subscribes to multiple events that will only fire once each
   *
   * @param handlers - Object mapping event names to their handlers
   * @returns Disposer function to unsubscribe from all events
   */
  once(handlers: EventRegistryObject<EventSpec>): EventDisposer;

  once<K extends keyof EventSpec>(
    arg0: K | EventRegistryObject<EventSpec>,
    handler?: EventCallback<EventSpec[K]>,
  ): EventDisposer {
    // Handle object syntax
    if (this.isEventRegistryObject(arg0)) {
      const disposers: EventDisposer[] = [];
      Object.entries(arg0).forEach(([eventName, eventHandler]) => {
        disposers.push(
          this.once(eventName as K, eventHandler as EventCallback<EventSpec[K]>),
        );
      });
      return () => disposers.forEach((d) => d());
    } else

    // Handle single event subscription
    if (handler) {
      const disposer = this.on<K>(
        arg0,
        function onceHandler(this: EventBus<EventSpec>, ...args) {
          handler.call(this, ...args);
          disposer();
        },
      );
      return disposer;
    } else

    // No-op case
    return () => false;
  }

  /**
   * Unsubscribes from events
   *
   * @param eventName - Event name to unsubscribe from (removes all handlers if no handler specified)
   * @param handler - Specific handler to remove (optional)
   */
  off<K extends keyof EventSpec>(eventName: K): void;
  off<K extends keyof EventSpec>(
    eventName: K,
    handler: EventCallback<EventSpec[K]>,
  ): void;

  /**
   * Unsubscribes from multiple events
   *
   * @param handlers - Object mapping event names to their handlers
   */
  off(handlers: EventRegistryObject<EventSpec>): void;

  /**
   * Unsubscribes from all events
   */
  off(): void;

  off<K extends keyof EventSpec>(
    arg0?: K | EventRegistryObject<EventSpec>,
    handler?: EventCallback<never>,
  ): void {
    if (!this.eventListeners) {
      return;
    }

    // Remove all event listeners
    if (arg0 === undefined) {
      for (const eventName in this.eventListeners) {
        this.removeEventListener(eventName);
      }
      return;
    }

    // Handle object syntax
    else if (this.isEventRegistryObject(arg0)) {
      Object.entries(arg0).forEach(([eventName, eventHandler]) => {
        this.removeEventListener(eventName as K, eventHandler as EventCallback<never>);
      });
      return;
    } else {
      // Handle single event unsubscription
      this.removeEventListener(arg0, handler);
    }

  }

  /**
   * Fires an event with optional data
   * All subscribed handlers will be called with the provided data
   *
   * @param eventName - Name of the event to fire
   * @param options - Data to pass to event handlers
   */
  fire<K extends keyof EventSpec>(eventName: K, options?: EventSpec[K]): void {
    if (!this.eventListeners) {
      return;
    }

    const listenersForEvent = this.eventListeners[eventName]?.concat();
    if (listenersForEvent) {
      for (let i = 0; i < listenersForEvent.length; i++) {
        // Single controlled cast back from the contravariant storage type.
        (listenersForEvent[i] as EventCallback<EventSpec[K]>).call(
          this,
          (options ?? {}) as EventSpec[K],
        );
      }
    }
  }

  /**
   * Removes event listener(s) for a specific event
   *
   * @param eventName - Event name
   * @param handler - Specific handler to remove (removes all if not provided)
   */
  private removeEventListener<K extends keyof EventSpec>(
    eventName: K,
    handler?: EventCallback<never>,
  ): void {
    if (!this.eventListeners[eventName]) {
      return;
    }

    if (handler) {
      const eventListener = this.eventListeners[eventName];
      const index = eventListener.indexOf(handler);
      if (index > -1) {
        eventListener.splice(index, 1);
      }
    } else {
      this.eventListeners[eventName] = [];
    }
  }

  /**
   * Initializes the event listeners object if not already initialized
   */
  private initializeEventListeners(): void {
    if (!this.eventListeners) {
      this.eventListeners = {} as Record<keyof EventSpec, EventCallback<never>[]>;
    }
  }

  /**
   * Ensures an event listener array exists for the given event name
   */
  private ensureEventListenerArray<K extends keyof EventSpec>(eventName: K): void {
    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = [];
    }
  }

  /**
   * Type guard to check if argument is an EventRegistryObject
   */
  private isEventRegistryObject(
    arg: unknown,
  ): arg is EventRegistryObject<EventSpec> {
    return typeof arg === 'object' && arg !== null;
  }
}
